import React, { useMemo } from 'react';
import Carousel from 'react-material-ui-carousel';
import { Box, Skeleton, useMediaQuery } from '@mui/material';
import OptimizedCarouselItem from './OptimizedItems';
import theme from '../../../theme';
import { useBanners, preloadCriticalBanners } from '../../../services/optimized-banner-service';

const CarouselHome = React.memo(() => {
  // Use responsive hook for proper breakpoint detection
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Fixed responsive heights
  const carouselHeight = useMemo(() => ({
    mobile: '250px',
    desktop: '515px'
  }), []);
  
  // Use optimized React Query hook
  const { data: banners, isLoading, error } = useBanners();
  
  // Preload critical banners when data is available
  React.useEffect(() => {
    if (banners && banners.length > 0) {
      preloadCriticalBanners(banners);
    }
  }, [banners]);
  
  // Memoized carousel configuration
  const carouselConfig = useMemo(() => ({
    indicatorIconButtonProps: {
      style: {
        width: '3px',
        height: '3px',
        borderRadius: '0',
        padding: '10px',
        color: theme.palette.secondary.main
      }
    },
    activeIndicatorIconButtonProps: {
      style: {
        backgroundColor: theme.palette.secondary.main
      }
    },
    indicatorContainerProps: {
      style: {
        position: 'absolute' as const,
        bottom: '5px',
        zIndex: 99,
        textAlign: 'center' as const
      }
    },
    autoPlay: true,
    animation: 'fade' as const,
    duration: 300,
    interval: 4000,
    swipe: true,
    navButtonsAlwaysVisible: false,
    cycleNavigation: true
  }), []);
  
  // Memoized carousel items
  const carouselItems = useMemo(() => {
    if (!banners || banners.length === 0) return [];
    
    return banners.map((banner, index) => (
      <OptimizedCarouselItem 
        key={banner.id} 
        banner={banner}
        index={index}
        isCritical={index < 2} // First 2 banners are critical
        height={isMobile ? carouselHeight.mobile : carouselHeight.desktop}
      />
    ));
  }, [banners, isMobile, carouselHeight]);
  
  // Loading state with fixed height
  if (isLoading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: isMobile ? carouselHeight.mobile : carouselHeight.desktop,
          position: 'relative',
          backgroundColor: '#f5f5f5'
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{
            backgroundColor: 'rgba(0,0,0,0.06)',
            borderRadius: 0
          }}
        />
      </Box>
    );
  }
  
  // Error state with fixed height
  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height: isMobile ? carouselHeight.mobile : carouselHeight.desktop,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          color: theme.palette.text.secondary,
          fontSize: '16px'
        }}
      >
        Unable to load banners
      </Box>
    );
  }
  
  // No banners state with fixed height
  if (!banners || banners.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          height: isMobile ? carouselHeight.mobile : carouselHeight.desktop,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f0f0f0',
          color: theme.palette.text.secondary,
          fontSize: '16px',
          borderRadius: 5
        }}
      >
        No banners available
      </Box>
    );
  }
  
  // Main carousel with fixed height container
  return (
    <Box
      sx={{
        width: '100%',
        height: isMobile ? carouselHeight.mobile : carouselHeight.desktop,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 5
      }}
    >
      <Carousel
        {...carouselConfig}
        sx={{
          width: '100%',
          height: '100%'
        }}
      >
        {carouselItems}
      </Carousel>
    </Box>
  );
});

export default CarouselHome;